import { AlertDialog, Separator, View, XStack, YStack } from 'tamagui'
import CobuText from './CobuText'
import CobuButton from './CobuButton'

export type CobuAlertAction = {
  text: string
  onPress: () => void
  style?: 'default' | 'cancel' | 'destructive'
}

export type CobuAlertProps = {
  open: boolean
  onOpenChange?: (open: boolean) => void
  title: string
  description: string
  acceptText?: string | undefined
  cancelText?: string | undefined
  onAccept?: () => void
  onCancel?: () => void
  actions?: CobuAlertAction[] // If provided, overrides acceptText/cancelText pattern
}

export default function CobuAlert({
  open,
  onOpenChange,
  title,
  description,
  acceptText,
  cancelText,
  onAccept,
  onCancel,
  actions: customActions,
}: CobuAlertProps) {
  // Use custom actions if provided, otherwise use accept/cancel pattern
  const actions: Array<{ text: string; onPress: () => void; isCancel: boolean; style?: 'default' | 'cancel' | 'destructive' }> = []
  
  if (customActions && customActions.length > 0) {
    // Use custom actions array
    customActions.forEach((action, index) => {
      actions.push({
        text: action.text,
        onPress: action.onPress,
        isCancel: action.style === 'cancel',
        style: action.style,
      })
    })
  } else {
    // Use legacy accept/cancel pattern
    if (cancelText) actions.push({ text: cancelText, onPress: onCancel ?? (() => {}), isCancel: true })
    actions.push({ text: acceptText ?? 'OK', onPress: onAccept ?? (() => {}), isCancel: false })
  }

  const isTwoButtonsRow = actions.length === 2

  const renderButton = (action: { text: string; onPress: () => void; isCancel: boolean; style?: 'default' | 'cancel' | 'destructive' }, index: number) => {
    const color = action.style === 'destructive' ? ('$error' as any) : ('$textLinkColor' as any)
    const Wrapper = action.isCancel ? AlertDialog.Cancel : AlertDialog.Action
    
    // Calculate border radius based on position
    const isLastButton = index === actions.length - 1
    const bottomRightRadius = isLastButton ? ('$4' as any) : 0
    const bottomLeftRadius = (isTwoButtonsRow && index === 0) || (!isTwoButtonsRow && isLastButton) ? ('$4' as any) : 0
    
    return (
      <Wrapper asChild key={`${action.text}-${index}`}>
        <CobuButton
          dsVariant="ghost"
          height={48}
          borderRadius={0}
          borderBottomLeftRadius={bottomLeftRadius}
          borderBottomRightRadius={bottomRightRadius}
          onPress={action.onPress}
          flex={isTwoButtonsRow ? 1 : undefined}
          hoverStyle={{
            borderBottomLeftRadius: bottomLeftRadius,
            borderBottomRightRadius: bottomRightRadius,
          }}
        >
          <CobuText variant="label" color={color} textAlign="center">
            {action.text}
          </CobuText>
        </CobuButton>
      </Wrapper>
    )
  }

  return (
    <AlertDialog open={open} onOpenChange={onOpenChange}>
      <AlertDialog.Portal>
        <AlertDialog.Overlay
          key="overlay"
          animation={undefined as any}
          opacity={0.45}
          enterStyle={{ opacity: 0 }}
          exitStyle={{ opacity: 0 }}
        />
        <AlertDialog.Content
          // bordered
          elevate
          key="content"
          bg="white"
          animation={undefined as any}
          enterStyle={{ x: 0, y: -20, opacity: 0, scale: 0.9 }}
          exitStyle={{ x: 0, y: 10, opacity: 0, scale: 0.95 }}
          x={0}
          scale={1}
          opacity={1}
          y={0}
          maxWidth={360}
          width={('100%' as any)}
          borderRadius={'$4' as any}
          borderWidth={0}
          borderColor={('$black' as any)}
          padding={0}
        >
          <YStack>
            <YStack padding="$5" gap="$3" alignItems="center">
              <CobuText variant="body" fow="$semibold" textAlign="center">
                {title}
              </CobuText>
              <CobuText variant="body" color={('$colorMuted' as any)} textAlign="center">
                {description}
              </CobuText>
            </YStack>

            <Separator borderColor={('$borderColorLight' as any)} />

            {isTwoButtonsRow ? (
              <XStack overflow="hidden" borderBottomLeftRadius={'$4' as any} borderBottomRightRadius={'$4' as any}>
                {renderButton(actions[0], 0)}
                <View width={1} backgroundColor={('$borderColorLight' as any)} />
                {renderButton(actions[1], 1)}
              </XStack>
            ) : (
              <YStack overflow="hidden" borderBottomLeftRadius={'$4' as any} borderBottomRightRadius={'$4' as any}>
                {actions.map((a, i) => (
                  <YStack key={`${a.text}-${i}`}>
                    {i > 0 ? <Separator borderColor={('$borderColorLight' as any)} /> : null}
                    {renderButton(a, i)}
                  </YStack>
                ))}
              </YStack>
            )}
          </YStack>
        </AlertDialog.Content>
      </AlertDialog.Portal>
    </AlertDialog>
  )
}